import { Button, ButtonText } from '@/components/ui/button'
import ChevronDownIcon from '@heroicons/react/16/solid/ChevronDownIcon'
import { AdjustmentsHorizontalIcon } from '@heroicons/react/24/solid'
import {
  ArrowDownRightIcon,
  ArrowPathRoundedSquareIcon,
  ArrowUpRightIcon,
  BeakerIcon,
  ChevronDoubleLeftIcon,
  PlusCircleIcon,
  QueueListIcon,
} from '@heroicons/react/16/solid'
import React from 'react'
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
} from '@/components/ui/DropdownMenu'
import {
  DropdownMenu,
  DropdownMenuRadioGroup,
  DropdownMenuTrigger,
} from '@radix-ui/react-dropdown-menu'
import useAppStore from '@/store/useAppStore'
import {
  BUDGETING_PERIODS,
  NON_MODAL_WINDOW_TYPE,
  PANEL_STATES,
} from '@/data/constants/constants'
import useAuthStore from '@/store/useAuthStore'
import ButtonGroup from '@/components/ButtonGroup'
import useOpenNonModalWndow from '@/hooks/useOpenNonModalWndow'
import useGetModelsList from '@/services/api/model/useGetModelsList'
import { Link, useNavigate } from 'react-router-dom'
import useGetTodoCount from '@/services/api/to-do/useGetTodoCount'
import useGetVaults from '@/services/api/vault/useGetVaults'
import _ from 'lodash'
import useGetPeriodByOffset from '@/services/api/cache/useGetPeriodByOffset'
import { isBefore } from 'date-fns'

export default function TopBar() {
  const panelState = useAppStore((state) => state.panelState)
  const togglePanelState = useAppStore((state) => state.togglePanelState)
  const avatar = useAuthStore((state) => state.getAvatar())
  const navigate = useNavigate()
  const numberOfPeriods = useAppStore((state) => state.numberOfPeriods)
  const setNumberOfPeriods = useAppStore((state) => state.setNumberOfPeriods)
  const periodType = useAppStore((state) => state.periodType)
  const setPeriodType = useAppStore((state) => state.setPeriodType)
  const offset = useAppStore((state) => state.offset)
  const setOffset = useAppStore((state) => state.setOffset)
  const { data: todoCount } = useGetTodoCount()

  const { data: modelsList } = useGetModelsList()

  const countEnabledModels = () => {
    return modelsList.filter((model) => model.is_enabled).length
  }

  const { data: vaults } = useGetVaults()

  const [offsetIncrement, setOffsetIncrement] = React.useState('1')
  const { open } = useOpenNonModalWndow()

  const { data: oldestPeriod } = useGetPeriodByOffset({
    offset,
    position: 0,
  })

  const scrollToCurrentPeriod = () => {
    const currentPeriod = document.querySelector('table th .current-period')
    if (currentPeriod) {
      currentPeriod.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center',
      })
    }
  }

  const vaultsFlat = _.flatten(
    vaults?.map((vault) => {
      return vault.is_group ? vault.vaults : [vault]
    }),
  )

  // oldest date of opening balance
  const oldestDateOfVault = _.minBy(
    vaultsFlat,
    (vault) => new Date(vault?.opening_balance?.transaction_date ?? Date.now()),
  )?.opening_balance?.transaction_date

  const disableBackButton =
    oldestDateOfVault &&
    oldestPeriod?.start &&
    isBefore(new Date(oldestPeriod.start), new Date(oldestDateOfVault))

  return (
    <div>
      <div className="flex justify-between">
        <div className="mb-2 flex items-center space-x-4 text-[hsl(var(--fill-gray-high))]">
          {panelState === PANEL_STATES.CLOSED && (
            <Button variant="ghost" className="relative px-1">
              <Button
                variant="avatar"
                className="h-[24px] rounded-full p-4"
                onClick={togglePanelState}
              >
                <ButtonText>{avatar}</ButtonText>
              </Button>
            </Button>
          )}

          <div className="flex items-center space-x-2">
            <ButtonGroup>
              <Button
                variant="outline"
                onClick={() => {
                  setOffset(parseInt(offset) + parseInt(offsetIncrement, 10))
                }}
                disabled={disableBackButton}
              >
                <ChevronDoubleLeftIcon />
                <ButtonText>
                  Show Previous: {offsetIncrement} {periodType}
                </ButtonText>
              </Button>
              <DropdownMenu>
                {/* Important to add asChild */}
                <DropdownMenuTrigger disabled={disableBackButton} asChild>
                  <Button iconOnly type="button" variant="outline">
                    <ChevronDownIcon />
                  </Button>
                </DropdownMenuTrigger>

                {periodType === BUDGETING_PERIODS.WEEK ? (
                  <WeekOffsetMenu
                    offset={offsetIncrement}
                    setOffset={(value) => {
                      setOffsetIncrement(value)
                      setOffset(parseInt(value) + parseInt(offset))
                    }}
                  />
                ) : (
                  <MonthOffsetMenu
                    offset={offsetIncrement}
                    setOffset={(value) => {
                      setOffsetIncrement(value)
                      setOffset(parseInt(value) + parseInt(offset))
                    }}
                  />
                )}
              </DropdownMenu>
            </ButtonGroup>
            <Button variant="outline" onClick={scrollToCurrentPeriod}>
              Today
            </Button>
          </div>
        </div>

        <div className="mb-2 flex items-center space-x-2  text-[hsl(var(--fill-gray-high))]">
          <Link to="/my-todos">
            <Button variant="outline">
              <QueueListIcon />
              <ButtonText>My Todos</ButtonText>
              <div className="flex min-w-5 items-center justify-center rounded-full bg-accent-tr-24 px-1.5 py-[0.125rem] text-xs text-txt-accent">
                <span>{todoCount ?? 0}</span>
              </div>
            </Button>
          </Link>
          <Link to="/money-paths">
            <Button variant="outline">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-split-icon lucide-split"
              >
                <path d="M16 3h5v5" />
                <path d="M8 3H3v5" />
                <path d="M12 22v-8.3a4 4 0 0 0-1.172-2.872L3 3" />
                <path d="m15 9 6-6" />
              </svg>
              <ButtonText>Money Paths</ButtonText>
            </Button>
          </Link>
          <Button
            variant="outline"
            onClick={() =>
              // open({
              //   name: NON_MODAL_WINDOW_TYPE.MODELS,
              // })
              navigate('/models')
            }
          >
            <BeakerIcon />
            <ButtonText>Models</ButtonText>
            <div className="flex size-5 items-center justify-center rounded-full bg-accent-tr-24 text-xs text-txt-accent">
              <span>{countEnabledModels()}</span>
            </div>
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" dropdown>
                <AdjustmentsHorizontalIcon />
                <ButtonText>View</ButtonText>
              </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent className="w-[220px] justify-start px-2 py-1 text-sm">
              <DropdownMenuLabel>Forecast Horizon</DropdownMenuLabel>
              <DropdownMenuSeparator />

              <DropdownMenuLabel>Weeks</DropdownMenuLabel>
              <DropdownMenuRadioGroup
                value={`${numberOfPeriods}-${periodType}`}
                onValueChange={(value) => {
                  const [number, type] = value.split('-')
                  setNumberOfPeriods(parseInt(number, 10))
                  setPeriodType(type)
                  setOffsetIncrement('1')
                  navigate('/')
                }}
              >
                <DropdownMenuRadioItem value={`13-${BUDGETING_PERIODS.WEEK}`}>
                  13 Weeks
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value={`26-${BUDGETING_PERIODS.WEEK}`}>
                  26 Weeks
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value={`52-${BUDGETING_PERIODS.WEEK}`}>
                  52 Weeks
                </DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>

              <DropdownMenuLabel>Months</DropdownMenuLabel>
              <DropdownMenuRadioGroup
                value={`${numberOfPeriods}-${periodType}`}
                onValueChange={(value) => {
                  const [number, type] = value.split('-')
                  setNumberOfPeriods(parseInt(number, 10))
                  setPeriodType(type)
                  setOffsetIncrement('1')
                  navigate('/')
                }}
              >
                <DropdownMenuRadioItem value={`6-${BUDGETING_PERIODS.MONTH}`}>
                  6 Months
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value={`12-${BUDGETING_PERIODS.MONTH}`}>
                  12 Months
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value={`24-${BUDGETING_PERIODS.MONTH}`}>
                  24 Months
                </DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button>
                <PlusCircleIcon />
                <ButtonText>Add Transaction</ButtonText>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={
                    () => navigate('/transactions/new?type=in')
                    // open({
                    //   name: NON_MODAL_WINDOW_TYPE.TRANSACTION,
                    //   type: 'income',
                    //   rerender: true,
                    // })
                  }
                >
                  <ArrowUpRightIcon />
                  <ButtonText>Money In</ButtonText>
                </Button>
              </DropdownMenuItem>
              <DropdownMenuItem>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={
                    () => navigate('/transactions/new?type=out')
                    // open({
                    //   name: NON_MODAL_WINDOW_TYPE.TRANSACTION,
                    //   type: 'expenses',
                    //   rerender: true,
                    // })
                  }
                >
                  <ArrowDownRightIcon />
                  <ButtonText>Money Out</ButtonText>
                </Button>
              </DropdownMenuItem>
              <DropdownMenuItem>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={
                    () => navigate('/transactions/new?type=transfer')
                    // open({
                    //   name: NON_MODAL_WINDOW_TYPE.TRANSACTION,
                    //   type: 'transfer',
                    //   rerender: true,
                    // })
                  }
                >
                  <ArrowPathRoundedSquareIcon />
                  <ButtonText>Transfer</ButtonText>
                </Button>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  )
}

export function WeekOffsetMenu({ offset, setOffset }) {
  return (
    <DropdownMenuContent className="w-[180px]">
      <DropdownMenuRadioGroup value={offset} onValueChange={setOffset}>
        <DropdownMenuRadioItem value={'1'}>1 Week</DropdownMenuRadioItem>
        <DropdownMenuRadioItem value={'2'}>2 Weeks</DropdownMenuRadioItem>
        <DropdownMenuRadioItem value={'4'}>4 Weeks</DropdownMenuRadioItem>
        <DropdownMenuRadioItem value={'13'}>13 Weeks</DropdownMenuRadioItem>
      </DropdownMenuRadioGroup>
    </DropdownMenuContent>
  )
}

export function MonthOffsetMenu({ offset, setOffset }) {
  return (
    <DropdownMenuContent className="w-[180px]">
      <DropdownMenuRadioGroup value={offset} onValueChange={setOffset}>
        <DropdownMenuRadioItem value={'1'}>1 Month</DropdownMenuRadioItem>
        <DropdownMenuRadioItem value={'2'}>2 Months</DropdownMenuRadioItem>
        <DropdownMenuRadioItem value={'3'}>3 Months</DropdownMenuRadioItem>
        <DropdownMenuRadioItem value={'6'}>6 Months</DropdownMenuRadioItem>
      </DropdownMenuRadioGroup>
    </DropdownMenuContent>
  )
}
